import React, { useState, useEffect } from 'react';
import { useAlert } from '../contexts/AlertContext';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Mail, Phone, MapPin } from 'lucide-react';
import { apiService } from '@/services/api';

const ContactPage: React.FC = () => {
  const { showAlert } = useAlert();
  const { t, i18n } = useTranslation();
  const [contactInfo, setContactInfo] = useState<any>({});

  useEffect(() => {
    apiService.contactInfo.get()
      .then(res => { if (res.data) setContactInfo(res.data); })
      .catch(() => {});
  }, []);

  const getAddress = () => {
    const lang = i18n.language;
    if (lang === 'zh-TW' || lang === 'zh-HK') return contactInfo.addressZhHk;
    if (lang === 'zh-CN') return contactInfo.addressZhCn;
    return contactInfo.addressEn || contactInfo.addressZhHk;
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement contact form submission
    showAlert({
      type: 'success',
      message: t('contactPage.successMessage'),
    });
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="flex flex-col">
      <main className="flex-1">
        <div className="w-full bg-background py-20 px-6">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Section */}
          <div className="space-y-12">
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                {t('contactPage.title')}
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                {t('contactPage.subtitle')}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {contactInfo.isEmailVisible !== false && (
                <Card className="bg-card text-card-foreground border-border shadow-sm">
                  <CardContent className="flex items-center gap-4 p-6">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">{t('contactPage.emailLabel')}</p>
                      <p className="text-foreground font-semibold break-all">{contactInfo.email || t('contactPage.emailValue')}</p>
                    </div>
                  </CardContent>
                </Card>
              )}
              {contactInfo.isPhoneVisible !== false && (
                <Card className="bg-card text-card-foreground border-border shadow-sm">
                  <CardContent className="flex items-center gap-4 p-6">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">{t('contactPage.phoneLabel')}</p>
                      <p className="text-foreground font-semibold">{contactInfo.phone || t('contactPage.phoneValue')}</p>
                    </div>
                  </CardContent>
                </Card>
              )}
              {contactInfo.isAddressVisible !== false && (
                <Card className="sm:col-span-2 bg-card text-card-foreground border-border shadow-sm">
                  <CardContent className="flex items-center gap-4 p-6">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">{t('contactPage.addressLabel')}</p>
                      <p className="text-foreground font-semibold">{getAddress() || t('contactPage.addressValue')}</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Right Section - Form */}
          <div className="bg-card text-card-foreground rounded-2xl shadow-sm border border-border p-8 md:p-10">
            <div className="space-y-8">
              <h2 className="text-2xl font-bold tracking-tight text-foreground">{t('contactPage.formTitle')}</h2>
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <Input
                    type="text"
                    name="name"
                    placeholder={t('contactPage.namePlaceholder')}
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="h-12 text-base bg-background text-foreground border-input"
                  />
                  <Input
                    type="email"
                    name="email"
                    placeholder={t('contactPage.emailPlaceholder')}
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="h-12 text-base bg-background text-foreground border-input"
                  />
                  <Input
                    type="text"
                    name="subject"
                    placeholder={t('contactPage.subjectPlaceholder')}
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="h-12 text-base bg-background text-foreground border-input"
                  />
                  <Textarea
                    name="message"
                    placeholder={t('contactPage.messagePlaceholder')}
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    required
                    className="resize-none text-base p-4 bg-background text-foreground border-input"
                  />
                </div>
                <Button type="submit" size="lg" className="w-full text-lg h-12">
                  {t('contactPage.submitButton')}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
      </main>
    </div>
  );
};

export default ContactPage;
